import { useState } from 'react';
import { privacyApi } from '../../../api/privacy.js';
import { usePaginatedQuery } from '../../../hooks/usePaginatedQuery.js';
import { DataTable } from '../../../components/DataTable.jsx';
import { Pagination } from '../../../components/ui/Pagination.jsx';
import { PageHeader } from '../../../components/ui/PageHeader.jsx';
import { Select } from '../../../components/ui/Select.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { Modal } from '../../../components/ui/Modal.jsx';
import { Tabs } from '../../../components/ui/Tabs.jsx';
import { fmt } from '../../../lib/formatters.js';

const STATUS_TONE = { pending: 'warning', completed: 'success', failed: 'danger', ready: 'info', downloaded: 'success', expired: 'neutral' };

const ERASURE_STATUS = [
  { value: '', label: 'Tutti' },
  { value: 'pending', label: 'In attesa' },
  { value: 'completed', label: 'Completate' },
  { value: 'failed', label: 'Fallite' }
];

const EXPORT_STATUS = [
  { value: '', label: 'Tutti' },
  { value: 'ready', label: 'Pronte' },
  { value: 'downloaded', label: 'Scaricate' },
  { value: 'expired', label: 'Scadute' },
  { value: 'failed', label: 'Fallite' }
];

function OperationsModal({ open, onClose, request }) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={request ? `Operazioni richiesta ${String(request._id).slice(-8)}` : 'Operazioni'}
      description={request ? `Soggetto: ${request.subjectEmail || request.subjectId} - stato ${request.status}` : null}
      size="lg"
    >
      {request && Array.isArray(request.operations) && request.operations.length > 0 ? (
        <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-border text-sm">
          <thead className="bg-surface">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-ink-muted">Collezione</th>
              <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-ink-muted">Azione</th>
              <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider text-ink-muted">Conteggio</th>
              <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-ink-muted">Nota</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {request.operations.map((op, idx) => (
              <tr key={idx}>
                <td className="px-3 py-2"><code className="text-xs">{op.target || op.collection || '-'}</code></td>
                <td className="px-3 py-2"><Badge>{op.action}</Badge></td>
                <td className="px-3 py-2 text-right">{fmt.int(op.count || 0)}</td>
                <td className="px-3 py-2 text-xs text-ink-muted">{op.note || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      ) : (
        <p className="text-sm text-ink-muted">Nessuna operazione registrata.</p>
      )}
    </Modal>
  );
}

function ErasuresView() {
  const { items, isLoading, pagination, page, setPage, filters, setFilters } = usePaginatedQuery({
    key: ['privacy', 'erasures'],
    fetcher: privacyApi.listErasures
  });
  const [opsTarget, setOpsTarget] = useState(null);

  const columns = [
    { key: 'subjectEmail', header: 'Soggetto', render: (r) => <span className="text-sm text-ink">{r.subjectEmail || r.subjectId}</span> },
    { key: 'status', header: 'Stato', render: (r) => <Badge tone={STATUS_TONE[r.status] || 'neutral'}>{r.status}</Badge> },
    { key: 'reason', header: 'Motivo', render: (r) => <span className="text-xs text-ink-muted">{r.reason || '-'}</span> },
    { key: 'createdAt', header: 'Richiesta', render: (r) => <span className="text-xs text-ink-muted">{fmt.dateTime(r.createdAt)}</span> },
    { key: 'completedAt', header: 'Completata', render: (r) => r.completedAt ? <span className="text-xs text-ink-muted">{fmt.dateTime(r.completedAt)}</span> : <span className="text-xs text-ink-faint">-</span> },
    {
      key: 'ops',
      header: '',
      align: 'right',
      render: (r) => (
        <button type="button" className="text-xs text-ink-muted hover:text-ink" onClick={() => setOpsTarget(r)}>
          Operazioni ({(r.operations || []).length})
        </button>
      )
    }
  ];

  return (
    <>
      <div className="flex flex-wrap items-end gap-3">
        <Select
          label="Stato"
          className="w-44"
          value={filters.status || ''}
          onChange={(e) => setFilters({ status: e.target.value || undefined })}
          options={ERASURE_STATUS}
        />
      </div>
      <DataTable
        columns={columns}
        rows={items}
        loading={isLoading}
        empty={{ title: 'Nessuna richiesta', description: 'Le richieste di cancellazione GDPR appariranno qui.' }}
      />
      <Pagination pagination={pagination} onPageChange={setPage} />
      <OperationsModal open={Boolean(opsTarget)} onClose={() => setOpsTarget(null)} request={opsTarget} />
    </>
  );
}

function ExportsView() {
  const { items, isLoading, pagination, page, setPage, filters, setFilters } = usePaginatedQuery({
    key: ['privacy', 'exports'],
    fetcher: privacyApi.listDataExports
  });

  const columns = [
    { key: 'subjectEmail', header: 'Soggetto', render: (r) => <span className="text-sm text-ink">{r.subjectEmail || r.subjectId}</span> },
    { key: 'status', header: 'Stato', render: (r) => <Badge tone={STATUS_TONE[r.status] || 'neutral'}>{r.status}</Badge> },
    { key: 'byteSize', header: 'Dimensione', align: 'right', render: (r) => <span className="text-xs text-ink-muted">{fmt.int(r.byteSize)} byte</span> },
    { key: 'sha256', header: 'sha256', render: (r) => <code className="text-[10px] text-ink-muted">{r.sha256 ? `${r.sha256.slice(0, 16)}...` : '-'}</code> },
    { key: 'createdAt', header: 'Generata', render: (r) => <span className="text-xs text-ink-muted">{fmt.dateTime(r.createdAt)}</span> },
    { key: 'downloadedAt', header: 'Scaricata', render: (r) => r.downloadedAt ? <span className="text-xs text-ink-muted">{fmt.dateTime(r.downloadedAt)}</span> : <span className="text-xs text-ink-faint">-</span> },
    { key: 'expiresAt', header: 'Scade', render: (r) => r.expiresAt ? <span className="text-xs text-ink-muted">{fmt.dateTime(r.expiresAt)}</span> : <span className="text-xs text-ink-faint">-</span> }
  ];

  return (
    <>
      <div className="flex flex-wrap items-end gap-3">
        <Select
          label="Stato"
          className="w-44"
          value={filters.status || ''}
          onChange={(e) => setFilters({ status: e.target.value || undefined })}
          options={EXPORT_STATUS}
        />
      </div>
      <DataTable
        columns={columns}
        rows={items}
        loading={isLoading}
        empty={{ title: 'Nessuna esportazione', description: 'Le esportazioni dati GDPR appariranno qui.' }}
      />
      <Pagination pagination={pagination} onPageChange={setPage} />
    </>
  );
}

export function ErasuresPage() {
  const [tab, setTab] = useState('erasures');
  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Esercizio dei diritti GDPR"
        description="Richieste di cancellazione (art. 17) e esportazioni dati (art. 15)."
      />
      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { value: 'erasures', label: 'Cancellazioni' },
          { value: 'exports', label: 'Esportazioni' }
        ]}
      />
      {tab === 'erasures' ? <ErasuresView /> : <ExportsView />}
    </div>
  );
}
